export interface CallRecord {
  id: string;
  callerId: string;
  callerName: string;
  recipientId: string;
  recipientName: string;
  startTime: Date;
  endTime?: Date;
  status: 'active' | 'completed' | 'missed' | 'rejected';
  duration: number;
  quality: {
    average: number;
    samples: number[];
  };
}

class CallHistoryService {
  private static instance: CallHistoryService;
  private callHistory: CallRecord[] = [];

  private constructor() {
    // Initialize with empty call history since backend API doesn't exist yet
    this.loadCallHistory();
  }

  public static getInstance(): CallHistoryService {
    if (!CallHistoryService.instance) {
      CallHistoryService.instance = new CallHistoryService();
    }
    return CallHistoryService.instance;
  }

  public async loadCallHistory() {
    try {
      // Since the backend API doesn't exist yet, we'll use localStorage for now
      const savedHistory = localStorage.getItem('callHistory');
      if (savedHistory) {
        this.callHistory = JSON.parse(savedHistory).map((call: any) => ({
          ...call,
          startTime: new Date(call.startTime),
          endTime: call.endTime ? new Date(call.endTime) : undefined
        }));
      } else {
        this.callHistory = [];
      }
    } catch (error) {
      console.error('Error loading call history:', error);
      // Initialize with empty array if there's an error
      this.callHistory = [];
    }
  }

  public async startCall(callerId: string, callerName: string, recipientId: string, recipientName: string): Promise<string> {
    const callRecord: CallRecord = {
      id: Math.random().toString(36).substr(2, 9),
      callerId,
      callerName,
      recipientId,
      recipientName,
      startTime: new Date(),
      status: 'completed',
      duration: 0,
      quality: {
        average: 0,
        samples: []
      }
    };

    this.callHistory.unshift(callRecord);
    await this.saveCallHistory();
    return callRecord.id;
  }

  public async endCall(callId: string, duration: number, quality: number) {
    const callRecord = this.callHistory.find(call => call.id === callId);
    if (callRecord) {
      callRecord.endTime = new Date();
      callRecord.duration = duration;
      callRecord.quality.samples.push(quality);
      callRecord.quality.average = callRecord.quality.samples.reduce((a, b) => a + b, 0) / callRecord.quality.samples.length;
      await this.saveCallHistory();
    }
  }

  public async updateCallQuality(callId: string, quality: number) {
    const callRecord = this.callHistory.find(call => call.id === callId);
    if (callRecord) {
      callRecord.quality.samples.push(quality);
      callRecord.quality.average = callRecord.quality.samples.reduce((a, b) => a + b, 0) / callRecord.quality.samples.length;
      await this.saveCallHistory();
    }
  }

  public async markCallAsMissed(callId: string) {
    const callRecord = this.callHistory.find(call => call.id === callId);
    if (callRecord) {
      callRecord.status = 'missed';
      await this.saveCallHistory();
    }
  }

  public async markCallAsRejected(callId: string) {
    const callRecord = this.callHistory.find(call => call.id === callId);
    if (callRecord) {
      callRecord.status = 'rejected';
      await this.saveCallHistory();
    }
  }

  private async saveCallHistory() {
    try {
      // Save to localStorage since backend API doesn't exist yet
      localStorage.setItem('callHistory', JSON.stringify(this.callHistory));
      
      // TODO: When backend API is ready, uncomment this:
      // await fetch('/api/calls/history', {
      //   method: 'POST',
      //   headers: {
      //     'Content-Type': 'application/json',
      //     'Authorization': `Bearer ${localStorage.getItem('token')}`
      //   },
      //   body: JSON.stringify({ calls: this.callHistory })
      // });
    } catch (error) {
      console.error('Error saving call history:', error);
    }
  }

  public getCallHistory(): CallRecord[] {
    return this.callHistory;
  }

  public getCallById(callId: string): CallRecord | undefined {
    return this.callHistory.find(call => call.id === callId);
  }
}

export default CallHistoryService; 